import { gql } from '@apollo/client';
import { WhatsApp } from 'constants/messageMediums';
import { useMemo } from 'react';
import { useGraphql } from 'utils/hooks/useGraphql';
import { isUndefined } from 'utils/tsHelpers';
import {
  DEFAULT_TYPEAHEAD_QUERY_LIMIT,
  DEFAULT_TYPEAHEAD_SORTING,
  DISPLAYED_CAMPAIGN_STATES,
} from '../constants';
import { CampaignMetadata } from '../HoldUntilReply.types';

export const GET_WHATS_APP_CAMPAIGNS_QUERY = gql`
  query getWhatsAppCampaigns (
    $campaignFilterInfo: CampaignFilterInfoInput
    $ids: [Long!]
    $pagination: Pagination
    $search: String
    $sort: Sort
  ) {
    campaigns(
      campaignFilterInfo: $campaignFilterInfo
      ids: $ids
      pagination: $pagination
      sort: $sort
      search: $search
    ) {
      info {
        count
      }
      results {
        id
        name
        templateId
      }
    }
  }`;

type UseCampaignsHookInputs = {
  search?: string;
  ids?: number[];
  page?: number;
};

type UseCampaignsHookOutput = {
  campaigns: CampaignMetadata[];
  campaignsCount: number;
  didError: boolean;
  isLoading: boolean;
};

type UseCampaignsQueryData = {
  campaigns: {
    info: {
      count: number;
    };
    results: CampaignMetadata[];
  } | null;
};

const useCampaignsVariables = (search?: string, ids?: number[], page?: number) => {
  return useMemo(
    () => ({
      search,
      ids,
      pagination: {
        ...DEFAULT_TYPEAHEAD_QUERY_LIMIT,
        offset: (page || 0) * DEFAULT_TYPEAHEAD_QUERY_LIMIT.limit,
      },
      sort: DEFAULT_TYPEAHEAD_SORTING,
      campaignFilterInfo: {
        campaignState: DISPLAYED_CAMPAIGN_STATES,
        messageMedium: WhatsApp,
      },
    }),
    [search, ids, page],
  );
};

export const useCampaignsQuery = ({
  search,
  ids,
  page = 0,
}: UseCampaignsHookInputs): UseCampaignsHookOutput => {
  const variables = useCampaignsVariables(search, ids, page);
  const skip = ids?.length === 0;
  const {
    data: { campaigns: data } = {},
    error: didError,
    isLoading,
  } = useGraphql<UseCampaignsQueryData>(GET_WHATS_APP_CAMPAIGNS_QUERY, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    variables,
    skip,
  });

  return {
    campaigns: isUndefined(data?.results) || skip ? [] : data.results,
    campaignsCount: data?.info?.count || 0,
    didError,
    isLoading,
  };
};

export const useSelectedCampaigns = (
  selectedCampaignIds: number[],
  search?: string,
  page?: number,
) =>
  useCampaignsQuery({
    search,
    ids: selectedCampaignIds,
    page,
  });

export const useSuggestedCampaigns = (search: string, page?: number) =>
  useCampaignsQuery({ search, page });
